import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthStore } from 'src/app/store/auth.store';
import { SupportChatStore } from 'src/app/pages/support-chat/services/support-chat.store';

// Route that already hosts the real chat launcher (SupportChatWidgetComponent).
// We hide this notice there so we don't double up.
const LAUNCHER_ROUTE = '/fund-account/crypto';
// How long the toast lingers on screen before auto-dismissing.
const AUTO_HIDE_MS = 4000;
// Exit animation duration — must match the .sp-chat-notice--leaving keyframe.
const LEAVE_ANIMATION_MS = 260;

@Component({
  standalone: true,
  selector: 'app-support-chat-notice',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (mounted()) {
      <button
        type="button"
        class="sp-chat-notice backdrop-blur-lg"
        [class.sp-chat-notice--leaving]="leaving()"
        (click)="open()"
        [attr.aria-label]="
          'Tienes ' + count() + ' mensaje' + (count() === 1 ? '' : 's') + ' de soporte sin leer'
        "
      >
        <span class="sp-chat-notice__icon" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          <span class="sp-chat-notice__dot" aria-hidden="true"></span>
        </span>
        <span class="sp-chat-notice__copy">
          <span class="sp-chat-notice__title">
            {{ count() === 1 ? '1 mensaje nuevo' : count() + ' mensajes nuevos' }}
          </span>
          <span class="sp-chat-notice__sub">Soporte ha respondido</span>
        </span>
        <svg
          class="sp-chat-notice__chevron"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
      </button>
    }
  `,
  styles: [
    `
      :host {
        font-family: 'Roboto', system-ui, sans-serif;
      }
      .sp-chat-notice {
        position: fixed;
        top: 72px;
        right: 16px;
        z-index: 150;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 8px 10px 8px 8px;
        max-width: 280px;
        border: 0;
        border-radius: 12px;
        background: white;
        color: #0f172a;
        box-shadow: 0 10px 30px -10px rgba(8, 37, 99, 0.28),
          0 2px 6px rgba(15, 23, 42, 0.06);
        cursor: pointer;
        text-align: left;
        font-family: inherit;
        animation: sp-chat-notice-in 280ms cubic-bezier(0.2, 0.7, 0.3, 1) both;
        transition: transform 140ms ease, box-shadow 140ms ease;
      }
      .sp-chat-notice:hover {
        transform: translateY(-1px);
        box-shadow: 0 14px 32px -10px rgba(8, 37, 99, 0.35),
          0 3px 10px rgba(15, 23, 42, 0.08);
      }
      .sp-chat-notice:active {
        transform: translateY(0);
      }
      .sp-chat-notice--leaving {
        animation: sp-chat-notice-out 260ms cubic-bezier(0.4, 0, 0.6, 1) forwards;
        pointer-events: none;
      }
      .sp-chat-notice__icon {
        position: relative;
        flex: 0 0 30px;
        width: 30px;
        height: 30px;
        border-radius: 8px;
        background: #1e5fd4;
        color: #ffffff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .sp-chat-notice__dot {
        position: absolute;
        top: -3px;
        right: -3px;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: #ef4444;
        border: 2px solid #ffffff;
      }
      .sp-chat-notice__copy {
        display: flex;
        flex-direction: column;
        line-height: 1.15;
        min-width: 0;
      }
      .sp-chat-notice__title {
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .sp-chat-notice__sub {
        font-size: 11px;
        margin-top: 2px;
      }
      .sp-chat-notice__chevron {
        flex: 0 0 14px;
        color: #94a3b8;
        margin-left: 2px;
      }
      @keyframes sp-chat-notice-in {
        from {
          opacity: 0;
          transform: translateY(-6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes sp-chat-notice-out {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(-6px);
        }
      }
      @media (max-width: 480px) {
        .sp-chat-notice {
          top: 70px;
          right: 12px;
          max-width: calc(100vw - 24px);
        }
      }
    `
  ]
})
export class SupportChatNoticeComponent implements OnInit, OnDestroy {
  private readonly store = inject(SupportChatStore);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly count = this.store.unreadCount;
  private readonly currentUrl = signal<string>('');
  private readonly autoHidden = signal<boolean>(false);
  // DOM presence is kept around for the exit animation; `leaving` toggles the
  // fade-out class while the element is on its way out.
  readonly mounted = signal<boolean>(false);
  readonly leaving = signal<boolean>(false);
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private leaveTimer: ReturnType<typeof setTimeout> | null = null;
  // Tracks the count we last "announced" so we only flash the toast on
  // *new* arrivals, not every time the signal touches the same value.
  private lastShownCount = 0;

  readonly visible = computed(() => {
    if (!this.authStore.user()) return false;
    if (this.count() === 0) return false;
    if (this.matchesLauncherRoute(this.currentUrl())) return false;
    if (this.autoHidden()) return false;
    return true;
  });

  constructor() {
    // Show the toast briefly each time the unread count goes UP — either on
    // initial load (when the store hydrates with N > 0 from the server) or
    // when a new admin message arrives via SSE while the user sits on the
    // home screen. Decreases (markRead → 0) just hide the toast naturally
    // through the `count() === 0` guard.
    effect(() => {
      const c = this.count();
      if (c > this.lastShownCount && c > 0) {
        this.autoHidden.set(false);
        if (this.hideTimer !== null) clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => {
          this.autoHidden.set(true);
          this.hideTimer = null;
        }, AUTO_HIDE_MS);
      }
      this.lastShownCount = c;
    });

    // Drive mount/leave from `visible()` so the exit fade-out can finish
    // before the element is removed from the DOM.
    effect(() => {
      const shouldShow = this.visible();
      if (shouldShow) {
        if (this.leaveTimer !== null) {
          clearTimeout(this.leaveTimer);
          this.leaveTimer = null;
        }
        this.leaving.set(false);
        this.mounted.set(true);
      } else if (this.mounted()) {
        this.leaving.set(true);
        if (this.leaveTimer !== null) clearTimeout(this.leaveTimer);
        this.leaveTimer = setTimeout(() => {
          this.mounted.set(false);
          this.leaving.set(false);
          this.leaveTimer = null;
        }, LEAVE_ANIMATION_MS);
      }
    });
  }

  ngOnInit(): void {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.leaveTimer !== null) {
      clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }
  }

  open(): void {
    // Reuse the widget's existing `?openChat=true` hook so the floating chat
    // pops up automatically on the crypto page without duplicating the
    // widget on every route.
    this.router.navigateByUrl(`${LAUNCHER_ROUTE}?openChat=true`);
  }

  private matchesLauncherRoute(url: string): boolean {
    if (!url) return false;
    return url.split('?')[0].split('#')[0] === LAUNCHER_ROUTE;
  }
}
